import { triggerSentryError, ComcastErrorType } from '../../../shared/helpers'
import configuration from '../configuration.js'
import NetworkCache from './networkCache'
import { store } from '../store'
import { registerUser } from '../actions/userActions'

const netCache = new NetworkCache()

export function clearNetCache() {
  netCache.clearCache()
}

export function appendUserId(url, pid) {
  let operator = -1 !== url.indexOf('?') ? '&' : '?'
  return `${url}${operator}user_id=${pid}`
}

export function baseRequest(
  url,
  method,
  auth,
  data,
  platformBypass,
  extraParams = {}
) {
  const cachedResponse = netCache.check(url, method)

  if (cachedResponse && !extraParams.ignoreCache) {
    if (cachedResponse.rejected) {
      return Promise.reject(cachedResponse.data)
    } else {
      return Promise.resolve(cachedResponse.data)
    }
  }

  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest()
    xhr.open(method, url)
    if (!platformBypass) {
      xhr.setRequestHeader('X-PBS-PlatformVersion', '3.0.0')
    }
    if (auth) {
      xhr.setRequestHeader('Authorization', auth)
    }

    if (extraParams.contentType) {
      xhr.setRequestHeader('Content-Type', extraParams.contentType)
    }

    xhr.onload = function () {
      const rejectObj = {
        status: this.status,
        response: this.response,
        statusText: xhr.statusText,
      }
      if (this.status >= 200 && this.status < 300) {
        try {
          if (xhr.response === '') {
            netCache.set({ url, method, data: '', rejected: false })
            resolve('')
          }
          const parsedResponse = JSON.parse(xhr.response)
          netCache.set({ url, method, data: parsedResponse, rejected: false })
          resolve(parsedResponse)
        } catch (error) {
          triggerSentryError('Could not parse response', 'utils.js', {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorType: ComcastErrorType.network,
          })
          netCache.set({ url, method, data: rejectObj, rejected: true })
          reject(rejectObj)
        }
      } else {
        netCache.set({ url, method, data: rejectObj, rejected: true })
        reject(rejectObj)
      }
    }
    xhr.onerror = function () {
      const rejectObj = {
        status: this.status,
        statusText: xhr.statusText,
      }

      netCache.set({ url, method, data: rejectObj, rejected: true })
      reject(rejectObj)
    }
    if (data) {
      xhr.send(JSON.stringify(data))
    } else {
      xhr.send()
    }
  })
}

export function contentServiceDelete(url, data) {
  return baseRequest(
    url,
    'DELETE',
    configuration.apiAuth.contentServicesAuth,
    data
  )
}
export function contentServicePatch(url, data) {
  return baseRequest(
    url,
    'PATCH',
    configuration.apiAuth.contentServicesAuth,
    data
  )
}
export function contentServicePut(url, data) {
  return baseRequest(
    url,
    'PUT',
    configuration.apiAuth.contentServicesAuth,
    data
  )
}
export function contentServiceGet(url) {
  return baseRequest(url, 'GET', configuration.apiAuth.contentServicesAuth)
}

function getProfileServiceAuth() {
  return COMPILING_FOR_COMCASTX1
    ? configuration.apiAuth.profileServicesAuthComcast
    : COMPILING_FOR_VIZIO
    ? configuration.apiAuth.profileServicesAuthVIZIO
    : configuration.apiAuth.profileServicesAuthSamsung
}
export function profileServiceGet(url) {
  return baseRequest(url, 'GET', getProfileServiceAuth())
}
export function profileServiceDelete(url) {
  return baseRequest(url, 'DELETE', getProfileServiceAuth())
}
export function authCodeRequest(deviceId, app) {
  const url = configuration.apiUrls.generateAuthCode
  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', getProfileServiceAuth())
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
    xhr.setRequestHeader('X-PBS-PlatformVersion', '3.0.0')
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        try {
          resolve(JSON.parse(xhr.response))
        } catch (error) {
          triggerSentryError('Could not parse authcode response', 'utils.js', {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorType: ComcastErrorType.network,
          })
          reject({
            status: this.status,
            statusText: xhr.statusText,
          })
        }
      } else {
        reject({
          status: this.status,
          statusText: xhr.statusText,
        })
      }
    }
    xhr.onerror = function () {
      reject({
        status: this.status,
        statusText: xhr.statusText,
      })
    }
    xhr.send(`deviceId=${deviceId}&app=${app}`)
  })
}

export const TEMPORAL_SETTINGS_AVAILABLE = {
  PASSPORT: 'passport',
  VPPA: 'vppa',
}

export function activateTemporarySetting(setting) {
  const { UID } = store.getState().device
  if (setting === TEMPORAL_SETTINGS_AVAILABLE.PASSPORT) {
    store.dispatch({ type: 'ACTIVATE_TEMPORARY_PASSPORT' })
  }
  if (setting === TEMPORAL_SETTINGS_AVAILABLE.VPPA) {
    store.dispatch({ type: 'ACTIVATE_TEMPORARY_VPPA' })
  }
  setTimeout(() => {
    store.dispatch({ type: 'DISABLE_TEMPORARY_SETTINGS' })
    registerUser(UID) // Refresh user credentials.
    clearNetCache() // Delete cache content so it doesn't have false negatives stored.
  }, 600000)
}

function getTvaultServiceAuth() {
  return TVAULT_AUTH_TOKEN
}

export function tvaultServiceGet(url) {
  return baseRequest(url, 'GET', getTvaultServiceAuth(), null, null, {
    ignoreCache: true,
  })
}

export function tvaultServicePost(url, data) {
  const contentType = 'application/vnd.api+json'
  return baseRequest(url, 'POST', getTvaultServiceAuth(), data, null, {
    contentType,
  })
}

function getMvaultServiceAuth() {
  return configuration.apiAuth.mvaultServicesAuth
}

export function mvaultService(url, method, data) {
  const contentType = 'application/json'
  return baseRequest(url, method, getMvaultServiceAuth(), data, true, {
    contentType,
  })
}

export function getConsentPayload(data) {
  const url = configuration.apiUrls.getConsentPayload
  const contentType = 'application/vnd.api+json'
  return baseRequest(url, 'POST', getTvaultServiceAuth(), data, null, {
    contentType,
  })
}

export function tvaultServicePatch(url, data) {
  const contentType = 'application/vnd.api+json'
  return baseRequest(url, 'PATCH', getTvaultServiceAuth(), data, null, {
    contentType,
  })
}
